// programsMenu.js
// Pure builder for the "Programs ▾" nav menu — replaces the separate
// Lessons and Journeys tabs with one dropdown after Collections that also
// reaches STEM Missions and Summer Camp days (previously reachable only
// from Home). Items the caller cannot access are simply left out of the
// returned array; the caller hides the whole menu when it comes back empty.
// A STEM Mission or Summer Camp day whose slug never resolves to a journey
// id is content this viewer doesn't have, so once the journey catalogue has
// loaded it is dropped rather than shown greyed out (and its section
// heading is dropped too if nothing in that block survives). While the
// catalogue is still loading, every entry looks unresolvable, so those
// entries are kept — disabled and marked `pending` — so the section doesn't
// pop in (or vanish for good on a failed fetch) once loading finishes. See
// docs/superpowers/specs/2026-09-16-home-redesign.md §11 WP10 (step 3).
import { STEM_MISSIONS, SUMMER_CAMP_DAYS } from "../home/launcherCatalog";
import journeyNote from "../home/journeyNote";

// A catalog entry resolves to an openable journey once useJourneyMap has
// matched its slug to a `/journeys?board=saras` row with a finite id.
const resolveJourney = (journeysBySlug, slug) => {
  const journey = journeysBySlug?.[slug] || null;
  const id = Number(journey?.id);
  return Number.isFinite(id) ? { journey, id } : { journey, id: null };
};

const buildCatalogItems = ({
  catalog,
  keyPrefix,
  titleField,
  journeysBySlug,
  openJourney,
  journeysLoading,
  t,
}) =>
  catalog.map((entry) => {
    const { journey, id } = resolveJourney(journeysBySlug, entry.slug);
    const resolvable = id !== null && typeof openJourney === "function";
    const item = {
      key: `${keyPrefix}-${entry.slug}`,
      label: t(entry[titleField]),
      icon: entry.icon,
      note: journeyNote(journey?.progress, t),
      disabled: !resolvable,
      onPress: resolvable ? () => openJourney(id) : undefined,
    };
    return journeysLoading && !resolvable ? { ...item, pending: true } : item;
  });

// A catalog block (its section heading + leaf items) as it should render
// for this journeysLoading state. While the journey catalogue is still
// being fetched, every entry looks unresolvable, so we keep them all,
// disabled and marked `pending`, rather than have the section pop in or
// out as the fetch resolves. Once loading is done, an item whose journey
// never resolved is content this viewer doesn't have — it and (if nothing
// in the block survives) its heading are simply left out.
const buildCatalogBlock = ({ heading, journeysLoading, items }) => {
  if (journeysLoading) return [heading, ...items];
  const openable = items.filter((item) => !item.disabled);
  return openable.length > 0 ? [heading, ...openable] : [];
};

// True when the Programs menu should render: at least one non-section item
// can actually be opened, or the journey catalogue is still resolving (in
// which case every unresolved item is `pending` and we don't yet know
// whether it will end up openable).
export const shouldShowProgramsMenu = (items = []) =>
  items.some((item) => item.type !== "section" && (!item.disabled || item.pending));

export const buildProgramItems = ({
  lessonPlanEnabled = false,
  learningLoopEnabled = false,
  canAccessStemMissions = false,
  canAccessSummerCampJourneys = false,
  journeysBySlug = {},
  journeysLoading = false,
  activeModule,
  openJourney,
  setActiveModule,
  t = (s) => s,
} = {}) => {
  const items = [];

  if (lessonPlanEnabled) {
    items.push({
      key: "lessons",
      label: t("Lessons"),
      icon: "clipboard-text-outline",
      active: activeModule === "lessonPlans",
      onPress: () => setActiveModule("lessonPlans"),
    });
  }

  if (learningLoopEnabled) {
    items.push({
      key: "journeys",
      label: t("Discovery Journeys"),
      icon: "map-marker-path",
      active: activeModule === "discoveryJourneys",
      onPress: () => setActiveModule("discoveryJourneys"),
    });
  }

  if (canAccessStemMissions && STEM_MISSIONS.length > 0) {
    items.push(
      ...buildCatalogBlock({
        heading: { type: "section", key: "stem-missions-section", label: t("STEM Missions") },
        journeysLoading,
        items: buildCatalogItems({
          catalog: STEM_MISSIONS,
          keyPrefix: "mission",
          titleField: "title",
          journeysBySlug,
          openJourney,
          journeysLoading,
          t,
        }),
      })
    );
  }

  if (canAccessSummerCampJourneys && SUMMER_CAMP_DAYS.length > 0) {
    items.push(
      ...buildCatalogBlock({
        heading: { type: "section", key: "summer-camp-section", label: t("Summer Camp") },
        journeysLoading,
        items: buildCatalogItems({
          catalog: SUMMER_CAMP_DAYS,
          keyPrefix: "day",
          titleField: "campTitle",
          journeysBySlug,
          openJourney,
          journeysLoading,
          t,
        }),
      })
    );
  }

  return items;
};

export default buildProgramItems;
